import React, {useEffect, useState} from 'react';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {Box} from '@mui/material';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurLocationTreeView, {
  TrackItemsType,
} from '../../eca-doc-list/eca-save-search/AurLocationTreeView';
import {t} from 'i18next';
import {MIN_SEARCH_CHAR} from '../../../constants/constants';
import {
  getSavedSearchDetails,
  getSavedSearchGroupsRoot,
} from '../../../api/EcaServices';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {AxiosError} from 'axios';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {ExtendedTreeItemProps} from '../../eca-doc-list/eca-save-search/EcaSaveSearch';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {IFilterSet} from '../../eca-doc-list/eca-doc-list.util';
import {
  extractSavedSearchId,
  getExpandableIds,
  getSavedSearchResFromEntity,
  getTreeObjectFromSavedSearch,
  isEmpty,
} from '../../../utils/utils';
import {SavedSearchRes} from '../../eca/SavedSearchPanel';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

export interface SavedSearchDetails {
  ecaSearchId: string;
  fieldTemplateId: number;
  name: string;
  id: string;
  useAsScope: boolean;
  createdDetail: {
    createdUserId: string;
    createdUserName: string;
    createdTime: string;
  };
  updatedDetail: {
    updatedUserId: string;
    updatedUserName: string;
    updatedTime: string;
  };
  lastRunDetail: {
    lastRunUserId: string;
    lastRunUserName: string;
    lastRunTime: string;
  };
  type: string;
  queryBuilderFlag: boolean;
  jsonLogicQuery: string;
  dtSearchQuery: string;
  query: string;
  filterSet: IFilterSet;
  docCount: number;
  filteredDocCount: number;
  notes: string;
  isPublicFlag: boolean;
  applyFilter: boolean;
  scope: {
    scopeType: string;
    scopeId: string;
    scopeName: string;
  };
  columnOrder: string[];

  sortOrder: {
    Column: string;
    Direction: string;
  }[];
}

interface SavedSearchesTabProps {
  selectedSavedSearchItem: (
    selectedNode: string,
    details: SavedSearchDetails
  ) => void;
  setExpandedIds?: (data: string[]) => void;
  trackItems: (items: TrackItemsType[]) => void;
  onSavedSearchSelection: (isLoading: boolean) => void;
}
const SavedSearchesTab: React.FC<SavedSearchesTabProps> = ({
  selectedSavedSearchItem,
  setExpandedIds,
  trackItems,
  onSavedSearchSelection,
}) => {
  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const {trackGAEvent} = useAnalytics();

  const {showToast} = useToast();

  const [saveSearchedData, setSaveSearchedData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);

  const [currentExpandedIds, setCurrentExpandedIds] = useState(['']);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    fetchSaveSearchGroupsRoot(
      searchKeyword
        ? {searchTerm: searchKeyword, indexName: 'Standard Index'}
        : {}
    );
    if (searchKeyword !== '') {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SEARCH,
        click: {
          name: t('gaTracking.ecaHomePage.filterList'),
          region: GA_REGIONS.LEFT_NAV_TAB,
          type: GA_CLICK_TYPES.LIST,
          action: t('gaTracking.ecaHomePage.searchedToFilter'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.savedSearch'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
    }
  }, [searchKeyword]);

  /**
   * Fetch saved search Root API
   */
  const fetchSaveSearchGroupsRoot = async (
    params: Record<string, unknown> = {}
  ) => {
    if (clientId && projectId) {
      try {
        const result = await getSavedSearchGroupsRoot({
          clientId,
          projectId,
          ...params,
        });
        const {data: savedSearchData} = result.data;

        if (!isEmpty(savedSearchData)) {
          const resData = getSavedSearchResFromEntity(
            savedSearchData?.[0] ?? {}
          );

          const mappedArray = [resData].map(
            (item: SavedSearchRes, index: number) =>
              getTreeObjectFromSavedSearch(item, index, item?.groupId ?? '')
          );

          if (!isEmpty(searchKeyword)) {
            setCurrentExpandedIds(getExpandableIds(mappedArray));
          } else {
            setCurrentExpandedIds(['']);
          }

          setSaveSearchedData(mappedArray);
        } else {
          setSaveSearchedData([]);
        }

        setLoading(false);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center', undefined, {
          code: err?.code,
        });
        setLoading(false);
      }
    }
  };

  const fetchSavedSearchDetails = async (id: string) => {
    onSavedSearchSelection?.(true);
    if (clientId && projectId) {
      try {
        const params = `?clientId=${clientId}&projectId=${projectId}`;
        const result = await getSavedSearchDetails(
          params,
          extractSavedSearchId(id)
        );
        selectedSavedSearchItem(id, result.data.data);
        onSavedSearchSelection?.(false);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center', undefined, {
          code: err?.code,
        });
        onSavedSearchSelection?.(false);
      }
    }
  };

  /**
   * Handle selected tree node
   * @param selectedNode
   * @param event
   */
  const handleSelectedItem = (
    selectedNode: TreeViewBaseItem<ExtendedTreeItemProps>,
    event?: React.SyntheticEvent
  ) => {
    if (selectedNode.fileType !== 'folder') {
      if (event) {
        fetchSavedSearchDetails(selectedNode.id);
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.ecaHomePage.savedSearchClick'),
            region: GA_REGIONS.WIDGET_LEFT_NAV,
            type: GA_CLICK_TYPES.LIST_ITEM,
            action: t('gaTracking.ecaHomePage.clickedASavedSearch'),
          },
          subPage: {
            subpage_name: t('gaTracking.ecaHomePage.savedSearches'),
            subpage_type: GA_SUBPAGE_TYPE.TAB,
          },
        });
      }
    }
  };
  const handleTrackItems = (items: TrackItemsType[]) => {
    if (items.length > 0) {
      trackItems(items);
    }
  };

  return (
    <Box>
      <Box className="search-container" data-testid="search-container">
        <AurSearchBox
          label={t('common.filterList')}
          minCharForSearch={MIN_SEARCH_CHAR}
          onSearch={(keyword: string) => {
            setSearchKeyword(keyword);
          }}
          classNameProps="filter-text-field"
        />
      </Box>

      <Box
        className="drawer-treeview-content"
        data-testid="location-treeview-content"
      >
        {loading ? (
          <AurLoadingIndicator show={true} />
        ) : (
          <div className="filtered-saved-search">
            <AurLocationTreeView
              itemsList={saveSearchedData}
              defaultExpandedIds={currentExpandedIds}
              handleSelectedItem={handleSelectedItem}
              setExpandedFolders={setExpandedIds}
              onTrackItemsUpdate={handleTrackItems}
              showTooltip={true}
              showTooltipAsNodeName={true}
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

export default SavedSearchesTab;
